import { useEffect, useRef, useState } from "react";
import { pickPreferredStrike } from "@/domains/watches/lib/watchOptionContracts";
import type { ApiFn } from "@/shared/lib/types";

const CHAIN_SUMMARY_CACHE_TTL_MS = 60_000;

export type OptionType = "call" | "put";
export type ExpiryStrikeMap = Record<string, string[]>;
export type ExpiryStrikesByType = Record<OptionType, ExpiryStrikeMap>;

type OptionsChainResponse = {
  summary?: {
    expiry_strike_map?: Record<string, Array<string | number>>;
    expiry_strikes_by_type?: Partial<
      Record<OptionType, Record<string, Array<string | number>>>
    >;
    expiries?: unknown[];
    underlying_price?: number;
    standard_contracts_only?: boolean;
  };
  results?: Array<{
    details?: {
      expiration_date?: string;
      strike_price?: number | string;
      contract_type?: string;
      shares_per_contract?: number;
    };
    underlying_asset?: {
      price?: number;
    };
  }>;
};

export function useWatchOptionChain(params: {
  api?: ApiFn;
  ticker: string;
  setContractExpiry?: (value: string) => void;
  setContractStrike?: (value: string) => void;
  /**
   * Legacy authoring surfaces selected the first expiry and a near-spot strike.
   * New customer-facing forms must leave contract facts for the user to choose.
   */
  autoSelect?: boolean;
}) {
  const [loadingExpiration, setLoadingExpiration] = useState(false);
  const [expiryStrikeMap, setExpiryStrikeMap] = useState<ExpiryStrikeMap>({});
  const [expiryStrikesByType, setExpiryStrikesByType] = useState<ExpiryStrikesByType>({
    call: {},
    put: {},
  });
  const [expDates, setExpDates] = useState<string[]>([]);
  const [chainLoadedForTicker, setChainLoadedForTicker] = useState("");
  const [chainStatus, setChainStatus] = useState("");
  const [chainError, setChainError] = useState("");
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);
  const requestIdRef = useRef(0);
  const summaryCacheRef = useRef<
    Record<
      string,
      {
        cachedAt: number;
        expDates: string[];
        expiryStrikeMap: ExpiryStrikeMap;
        expiryStrikesByType: ExpiryStrikesByType;
        currentPrice: number | null;
      }
    >
  >({});

  const resetChain = () => {
    requestIdRef.current += 1;
    setLoadingExpiration(false);
    setChainLoadedForTicker("");
    setChainStatus("");
    setChainError("");
    setExpDates([]);
    setExpiryStrikeMap({});
    setExpiryStrikesByType({ call: {}, put: {} });
    setCurrentPrice(null);
  };

  const applyCachedChain = (normalizedTicker: string, cached: {
    expDates: string[];
    expiryStrikeMap: ExpiryStrikeMap;
    expiryStrikesByType: ExpiryStrikesByType;
    currentPrice: number | null;
  }) => {
    setChainError("");
    setExpiryStrikeMap(cached.expiryStrikeMap);
    setExpiryStrikesByType(cached.expiryStrikesByType);
    setExpDates(cached.expDates);
    setChainLoadedForTicker(normalizedTicker);
    setCurrentPrice(cached.currentPrice);
    if (params.autoSelect !== false && cached.expDates.length > 0) {
      params.setContractExpiry?.(cached.expDates[0]);
      const strikes = cached.expiryStrikeMap[cached.expDates[0]] || [];
      if (strikes.length > 0) {
        params.setContractStrike?.(pickPreferredStrike(strikes, cached.currentPrice));
      }
    }
    setChainStatus(`Contracts are ready for ${normalizedTicker}. Choose an expiration and strike.`);
  };

  const getExpiryDate = async (query?: string, forceRefresh = false) => {
    let requestId = 0;
    let normalizedTicker = "";
    try {
      const tickerToFetch = query || params.ticker;
      if (!tickerToFetch?.trim() || !params.api) return;
      normalizedTicker = tickerToFetch.trim().toUpperCase();
      requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      const cached = summaryCacheRef.current[normalizedTicker];
      const cacheIsFresh =
        !forceRefresh &&
        !!cached &&
        Date.now() - cached.cachedAt <= CHAIN_SUMMARY_CACHE_TTL_MS;

      if (cacheIsFresh && cached) {
        if (requestId !== requestIdRef.current) return;
        applyCachedChain(normalizedTicker, cached);
        return cached.expDates;
      }

      setLoadingExpiration(true);
      setChainError("");
      setChainStatus(`Loading contracts for ${normalizedTicker}...`);
      setCurrentPrice(null);
      const data = await params.api<OptionsChainResponse>(
        "GET",
        `/api/options-chain?ticker=${encodeURIComponent(normalizedTicker)}&summary=1`,
        undefined,
        { silent: true },
      );
      if (requestId !== requestIdRef.current) return;

      let map: ExpiryStrikeMap = {};
      let mapByType: ExpiryStrikesByType = { call: {}, put: {} };
      let dates: string[] = [];
      let price: number | null = null;

      if (data?.summary && typeof data.summary === "object") {
        const summaryMap = data.summary.expiry_strike_map;
        if (summaryMap && typeof summaryMap === "object") {
          map = Object.entries(summaryMap).reduce(
            (acc, [date, strikes]) => {
              if (!Array.isArray(strikes)) return acc;
              acc[date] = strikes
                .map((value) => String(value))
                .sort((a, b) => Number(a) - Number(b));
              return acc;
            },
            {} as Record<string, string[]>,
          );
          const summaryDates = Array.isArray(data.summary.expiries)
            ? data.summary.expiries.filter(
                (value: unknown) => typeof value === "string" && !!map[value],
              ) as string[]
            : [];
          dates = summaryDates.length > 0 ? summaryDates : Object.keys(map).sort();
        }
        const summaryByType = data.summary.expiry_strikes_by_type;
        if (summaryByType && typeof summaryByType === "object") {
          mapByType = (["call", "put"] as const).reduce((byType, optionType) => {
            const typeMap = summaryByType[optionType];
            if (!typeMap || typeof typeMap !== "object") return byType;
            byType[optionType] = Object.entries(typeMap).reduce((acc, [date, strikes]) => {
              if (!Array.isArray(strikes)) return acc;
              acc[date] = strikes
                .map((value) => String(value))
                .sort((a, b) => Number(a) - Number(b));
              return acc;
            }, {} as ExpiryStrikeMap);
            return byType;
          }, { call: {}, put: {} } as ExpiryStrikesByType);
        }
        if (data.summary.standard_contracts_only === true) {
          const standardExpiries = new Set([
            ...Object.keys(mapByType.call),
            ...Object.keys(mapByType.put),
          ]);
          dates = dates.filter((date) => standardExpiries.has(date));
        }
        if (
          typeof data.summary.underlying_price === "number" &&
          Number.isFinite(data.summary.underlying_price)
        ) {
          price = data.summary.underlying_price;
        }
      }

      if (dates.length === 0 && Array.isArray(data?.results)) {
        const setMap: Record<string, Set<string>> = {};
        const setMapByType: Record<OptionType, Record<string, Set<string>>> = {
          call: {},
          put: {},
        };
        data.results.forEach((option) => {
          const date = option?.details?.expiration_date;
          const strikeRaw = option?.details?.strike_price;
          if (!date || strikeRaw === undefined || strikeRaw === null) return;
          const strike = String(Number(strikeRaw));
          if (!setMap[date]) setMap[date] = new Set<string>();
          setMap[date].add(strike);
          const optionType = option?.details?.contract_type?.toLowerCase();
          const standardContract = option?.details?.shares_per_contract === 100;
          if ((optionType === "call" || optionType === "put") && standardContract) {
            if (!setMapByType[optionType][date]) {
              setMapByType[optionType][date] = new Set<string>();
            }
            setMapByType[optionType][date].add(strike);
          }
        });
        map = Object.keys(setMap).reduce(
          (acc, date) => {
            acc[date] = Array.from(setMap[date]).sort(
              (a, b) => Number(a) - Number(b),
            );
            return acc;
          },
          {} as Record<string, string[]>,
        );
        dates = Object.keys(map).sort();
        mapByType = (["call", "put"] as const).reduce((byType, optionType) => {
          byType[optionType] = Object.entries(setMapByType[optionType]).reduce(
            (acc, [date, strikes]) => {
              acc[date] = Array.from(strikes).sort((a, b) => Number(a) - Number(b));
              return acc;
            },
            {} as ExpiryStrikeMap,
          );
          return byType;
        }, { call: {}, put: {} } as ExpiryStrikesByType);
        const fallbackPrice = data?.results?.[0]?.underlying_asset?.price;
        price = typeof fallbackPrice === "number" ? fallbackPrice : price;
      }

      if (dates.length === 0) {
        delete summaryCacheRef.current[normalizedTicker];
        setExpiryStrikeMap({});
        setExpiryStrikesByType({ call: {}, put: {} });
        setExpDates([]);
        if (params.autoSelect !== false) {
          params.setContractExpiry?.("");
          params.setContractStrike?.("");
        }
        setChainLoadedForTicker(normalizedTicker);
        setChainStatus(`No option contracts found for ${normalizedTicker}.`);
        return;
      }

      summaryCacheRef.current[normalizedTicker] = {
        cachedAt: Date.now(),
        expDates: dates,
        expiryStrikeMap: map,
        expiryStrikesByType: mapByType,
        currentPrice: price,
      };

      setExpiryStrikeMap(map);
      setExpiryStrikesByType(mapByType);
      setExpDates(dates);
      setChainLoadedForTicker(normalizedTicker);
      setCurrentPrice(price);
      if (params.autoSelect !== false) {
        params.setContractExpiry?.(dates[0]);
        const strikes = map[dates[0]] || [];
        if (strikes.length > 0) {
          params.setContractStrike?.(pickPreferredStrike(strikes, price));
        }
      }

      setChainStatus(`Contracts are ready for ${normalizedTicker}. Choose an expiration and strike.`);
      return dates;
    } catch {
      if (requestId !== requestIdRef.current) return;

      const cached = normalizedTicker
        ? summaryCacheRef.current[normalizedTicker]
        : undefined;
      if (cached) {
        applyCachedChain(normalizedTicker, cached);
        setChainError("Live refresh failed. Showing recent contract data.");
        return cached.expDates;
      }

      setExpiryStrikeMap({});
      setExpiryStrikesByType({ call: {}, put: {} });
      setExpDates([]);
      if (params.autoSelect !== false) {
        params.setContractExpiry?.("");
        params.setContractStrike?.("");
      }
      setChainStatus("");
      setChainError("Could not load option contracts. Try Load contracts again.");
    } finally {
      if (requestId === requestIdRef.current) {
        setLoadingExpiration(false);
      }
    }
  };

  useEffect(() => {
    return () => {
      requestIdRef.current += 1;
    };
  }, []);

  return {
    loadingExpiration,
    expiryStrikeMap,
    setExpiryStrikeMap,
    expiryStrikesByType,
    setExpiryStrikesByType,
    expDates,
    setExpDates,
    chainLoadedForTicker,
    setChainLoadedForTicker,
    chainStatus,
    setChainStatus,
    chainError,
    setChainError,
    currentPrice,
    setCurrentPrice,
    getExpiryDate,
    resetChain,
  };
}
